/* ow-ai-computer.js: drives snippets/ow-ai-computer.liquid, the AI employee's computer as the Omni Employees
   app shows it (computer/ComputerPanel.tsx, StepsList.tsx, tasks/DesktopPanel.tsx, chat/WorkingCard.tsx,
   chat/QuestionCard.tsx). Modes: static (tabs and the approval card only), autoplay (steps tick through while
   the component is on screen), interactive (the visitor starts playback). Autoplay stops at a
   "waiting" step until the visitor answers the approval card, as the employee would.
   Reduced motion: nothing moves on its own; explicit playback still works. No dependencies. */
(function () {
  'use strict';
  if (window.owComputerBooted) return;
  window.owComputerBooted = true;
  var activeRoot = null;
  var TICK_MS = 2200;
  var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function icon(name, extra) {
    return '<svg class="os-icon' + (extra ? ' ' + extra : '') + '" aria-hidden="true" focusable="false"><use href="#i-' + name + '"></use></svg>';
  }
  var MARK = {
    done: '<span class="os-tick" aria-hidden="true">' + icon('check') + '</span>',
    current: '<span class="os-spinner ch-checklist__spinner" aria-hidden="true"></span>',
    waiting: '<span class="ch-checklist__wait" aria-hidden="true">' + icon('info', 'os-icon--18') + '</span>',
    next: '<span class="os-checklist__ring" aria-hidden="true"></span>'
  };
  var WORD = { done: 'Done: ', current: 'In progress: ', waiting: 'Waiting: ', next: 'Next: ' };
  var TL_CLASS = { done: 'done', current: 'running', waiting: 'waiting', next: 'ended' };
  var TL_MARK = {
    done: ['wk-step__mark os-tick', icon('check')],
    current: ['wk-step__mark', '<span class="os-spinner wk-step__spinner"></span>'],
    waiting: ['wk-step__mark wk-step__mark--waiting', icon('help-circle', 'os-icon--16')],
    next: ['wk-step__mark wk-step__mark--ended', '']
  };

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function setText(el, text) { if (el) el.textContent = text; }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  /* Computer, Browser and Files: the kit's underline tabs, with the arrow keys between them (as in the app). */
  function initTabs(root) {
    var list = $(root, '[data-aic-tabs]');
    if (!list) return;
    var tabs = $$(list, '[role="tab"]');
    function select(i, focus) {
      tabs.forEach(function (tab, k) {
        var on = k === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        var pane = document.getElementById(tab.getAttribute('aria-controls'));
        if (pane) pane.hidden = !on;
      });
      if (focus) tabs[i].focus();
    }
    tabs.forEach(function (tab, i) { tab.addEventListener('click', function () { select(i, false); }); });
    list.addEventListener('keydown', function (e) {
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      var next = { ArrowRight: (i + 1) % tabs.length, ArrowLeft: (i - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 }[e.key];
      if (next === undefined) return;
      e.preventDefault();
      select(next, true);
    });
  }

  /* The chat layout: the header's computer toggle and the panel's close button, as in the app. */
  function initPanelToggle(root, name) {
    var toggle = $(root, '[data-aic-toggle]');
    var close = $(root, '[data-aic-close]');
    if (!toggle) return;
    function set(open) {
      root.classList.toggle('is-panel-closed', !open);
      // As ChatHeader.tsx draws it: secondary while the panel shows, ghost while it is closed.
      toggle.classList.toggle('os-btn--secondary', open);
      toggle.classList.toggle('os-btn--ghost', !open);
      toggle.setAttribute('aria-pressed', open ? 'true' : 'false');
      toggle.title = (open ? 'Hide ' : 'Show ') + name + '’s computer';
    }
    toggle.addEventListener('click', function () { set(root.classList.contains('is-panel-closed')); });
    if (close) close.addEventListener('click', function () { set(false); toggle.focus(); });
  }

  /* "Show the steps" / "Hide the steps" on the working card. */
  function initFold(root, count) {
    var btn = $(root, '[data-aic-fold]');
    if (!btn) return;
    var box = document.getElementById(btn.getAttribute('aria-controls'));
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (box) box.hidden = !open;
      btn.innerHTML = (open ? 'Hide the steps' : 'Show the steps (' + count + ')') + icon(open ? 'chevron-up' : 'chevron-down', 'os-icon--16');
    });
  }

  /* "Show the screen big": the screen moves into a dialog over the page and back, as the app's big screen
     takes the one live screen element with it. */
  function initBig(root, name) {
    var btn = $(root, '[data-aic-big]'), screen = $(root, '[data-aic-screen]');
    if (!btn) return;
    if (!screen || typeof HTMLDialogElement === 'undefined') { btn.hidden = true; return; }
    var dlg = document.createElement('dialog');
    dlg.className = 'ow-aic-big';
    dlg.setAttribute('aria-label', name + '’s computer');
    dlg.innerHTML = '<div class="ow-aic-big__head"><p class="ow-aic-big__title">' + escapeHtml(name) + '’s computer</p>' +
      '<button type="button" class="os-btn os-btn--ghost os-btn--icon os-btn--sm" aria-label="Back to the panel" title="Back to the panel">' +
      '<svg class="os-icon os-icon--16" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"/></svg></button></div>' +
      '<div class="ow-aic-big__stage"></div>';
    root.appendChild(dlg);
    var stage = $(dlg, '.ow-aic-big__stage'), home = screen.parentNode, after = screen.nextSibling;
    btn.addEventListener('click', function () { stage.appendChild(screen); dlg.showModal(); });
    $(dlg, 'button').addEventListener('click', function () { dlg.close(); });
    dlg.addEventListener('close', function () { home.insertBefore(screen, after); btn.focus(); });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
  }

  /* The approval card: Approve, Decline, or an answer in the person's own words (which cancels the action,
     as the app's QuestionCard does). onDecide(kind, text), kind approved | declined | answered. */
  function wireAsk(root, onDecide) {
    var ask = $(root, '[data-aic-ask]');
    if (!ask) return null;
    var other = $(ask, '[data-aic-other]'), own = $(ask, '[data-aic-own]');
    var a = $(ask, '[data-aic-approve]'), d = $(ask, '[data-aic-decline]');
    if (a) a.addEventListener('click', function () { onDecide('approved', ''); });
    if (d) d.addEventListener('click', function () { onDecide('declined', ''); });
    if (other && own) {
      other.addEventListener('click', function () {
        var open = other.getAttribute('aria-expanded') !== 'true';
        other.setAttribute('aria-expanded', open ? 'true' : 'false');
        own.hidden = !open;
        other.innerHTML = 'Answer in your own words' + icon(open ? 'chevron-up' : 'chevron-down', 'os-icon--16');
        if (open) { var t = $(own, 'textarea'); if (t) t.focus(); }
      });
      own.addEventListener('submit', function (e) {
        e.preventDefault();
        var t = $(own, 'textarea'), text = t ? t.value.trim() : '';
        if (!text) { if (t) t.focus(); return; }
        onDecide('answered', text);
      });
    }
    return ask;
  }
  function showOutcome(ask, kind, text) {
    if (!ask) return;
    var outcome = $(ask, '[data-aic-outcome]'), own = $(ask, '[data-aic-own]'), other = $(ask, '[data-aic-other]');
    ask.classList.toggle('is-decided', !!kind);
    if (own && kind) own.hidden = true;
    if (other && own && own.hidden) {
      other.setAttribute('aria-expanded', 'false');
      other.innerHTML = 'Answer in your own words' + icon('chevron-down', 'os-icon--16');
    }
    if (!outcome) return;
    outcome.hidden = !kind;
    outcome.className = 'os-status ch-msg__outcome' + (kind === 'approved' ? ' os-status--success' : '') + (kind === 'answered' ? ' ch-msg__outcome--said' : '');
    if (kind === 'answered') outcome.innerHTML = '<span class="os-label">You answered</span> <span class="wk-prose">' + escapeHtml(text) + '</span>';
    else outcome.textContent = kind === 'approved' ? 'You approved it.' : kind === 'declined' ? 'You declined it.' : '';
  }

  /* Narration (optional; off until the visitor taps "Play with voice", because browsers block autoplay with sound).
     The clips ow-aic-<page-handle>-<nn>.mp3 play in order. Each clip names the beat it plays under; the scene's
     adapter moves the screen to that beat (ad.cue) and says how long the beat's own screen changes need at their
     normal speed, so the next clip starts when the current one has ended, the beat has played, and a 300 ms breath
     has passed. Captions are shown under the scene. A missing or blocked clip is skipped silently: its caption
     stays up for a reading time and the scene carries on. Used by this file and by ow-home-computer.js.
     Adapter: begin() resets the scene into voice mode, cue(beat) returns the minimum dwell in ms, hold(bool) is
     pause/resume, finish() is the end after the approved clip, end() leaves voice mode. */
  var activeVoice = null;
  function createVoice(root, ad) {
    var bar = $(root, '[data-aic-voice-bar]');
    var tog = bar && $(bar, '[data-aic-voice]');
    var dataEl = bar && $(bar, '[data-aic-voice-data]');
    if (!bar || !tog || !dataEl) return null;
    var data = null;
    try { data = JSON.parse(dataEl.textContent); } catch (x) { return null; }
    if (!data || !data.handle || !data.clips || !data.clips.length) return null;
    var base = dataEl.getAttribute('data-base') || '';
    base = base.slice(0, base.lastIndexOf('/') + 1);
    var clips = data.clips, last = clips.length - 1, hasApproved = clips[last].beat === 'approved';
    var before = hasApproved ? last - 1 : last;
    var pauseBtn = $(bar, '[data-aic-voice-pause]'), stopBtn = $(bar, '[data-aic-voice-stop]');
    var cap = $(bar, '[data-aic-vcap]'), label = $(bar, '[data-aic-voice-label]');
    var on = false, paused = false, mode = 'idle', c = 0, run = 0, t0 = 0, dwell = 0;
    var audio = null, timer = null, failTimer = null, capTimer = null, ctl = {};

    function url(n) { return base + 'ow-aic-' + data.handle + '-' + (n < 10 ? '0' : '') + n + '.mp3'; }
    function clearTimers() { clearTimeout(timer); clearTimeout(failTimer); clearTimeout(capTimer); timer = failTimer = capTimer = null; }
    function quiet() {
      clearTimers();
      if (audio) { audio.onended = audio.onerror = audio.onplaying = null; try { audio.pause(); } catch (x) {} }
    }
    function ui() {
      tog.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (label) label.textContent = on ? 'Voice on' : 'Play with voice';
      if (pauseBtn) { pauseBtn.hidden = !on; pauseBtn.textContent = paused ? 'Resume voice' : 'Pause voice'; }
      if (stopBtn) stopBtn.hidden = !on;
    }
    function caption(text) { if (cap) { cap.textContent = text || ''; cap.hidden = !text; } }
    /* Plays one clip and calls then() once it has ended (or failed). then(delay) gets the breath to wait. */
    function playClip(clip, myRun, then) {
      var el = audio || (audio = new Audio());
      var settled = false;
      function settle(failed) {
        if (settled || myRun !== run) return;
        settled = true;
        clearTimeout(failTimer); clearTimeout(capTimer);
        el.onended = el.onerror = el.onplaying = null;
        var wait = failed ? Math.max(1800, clip.text.split(/\s+/).length * 320) : 0;
        then(wait);
      }
      el.onplaying = function () { clearTimeout(failTimer); };
      el.onended = function () { settle(false); };
      el.onerror = function () { settle(true); };
      failTimer = setTimeout(function () { if (el.paused || el.currentTime === 0) settle(true); }, 4200);
      capTimer = setTimeout(function () { settle(false); }, 20000);
      try {
        el.src = url(clip.n);
        el.playbackRate = 1;
        var attempt = el.play();
        if (attempt && attempt.catch) attempt.catch(function () { settle(true); });
      } catch (x) { settle(true); }
    }
    function play(k, myRun) {
      if (myRun !== run || !on) return;
      c = k;
      if (k > before) { mode = 'idle'; return; }          /* the scene now waits for the approval, as it does silently */
      var clip = clips[k];
      dwell = ad.cue(clip.beat) || 0;
      t0 = Date.now();
      caption(clip.text);
      mode = 'clip';
      playClip(clip, myRun, function (wait) {
        mode = 'gap';
        var rest = Math.max(300, dwell - (Date.now() - t0), wait);
        timer = setTimeout(function () { play(k + 1, myRun); }, rest);
      });
    }
    function start() {
      if (activeVoice && activeVoice !== ctl) activeVoice.stop();
      activeVoice = ctl;
      on = true; paused = false; run += 1; mode = 'idle'; c = 0;
      ad.begin();
      ui();
      play(0, run);
    }
    function stop() {
      run += 1;
      quiet();
      var was = on;
      on = false; paused = false; mode = 'idle';
      if (activeVoice === ctl) activeVoice = null;
      caption('');
      ui();
      if (was) ad.end();
    }
    function pause() {
      if (!on || paused) return;
      paused = true;
      clearTimeout(timer); clearTimeout(failTimer); clearTimeout(capTimer);
      if (audio && mode === 'clip') { try { audio.pause(); } catch (x) {} }
      ad.hold(true);
      ui();
    }
    function resume() {
      if (!on || !paused) return;
      paused = false;
      ad.hold(false);
      ui();
      var myRun = run;
      if (mode === 'gap') play(c + 1, myRun);
      else if (mode === 'clip') {
        if (audio && audio.src && !audio.ended) {
          var el = audio;
          failTimer = setTimeout(function () { if (el.paused) { el.onended = el.onerror = null; mode = 'gap'; play(c + 1, myRun); } }, 4200);
          try { var a = el.play(); if (a && a.catch) a.catch(function () { mode = 'gap'; play(c + 1, myRun); }); } catch (x) { mode = 'gap'; play(c + 1, myRun); }
        } else { mode = 'gap'; play(c + 1, myRun); }
      }
    }
    /* The visitor answered the approval card. Approve plays the last clip; any other answer says nothing more. */
    ctl.decision = function (kind) {
      if (!on) return;
      run += 1;
      var myRun = run;
      quiet();
      if (kind !== 'approved' || !hasApproved) { stop(); return; }
      paused = false; mode = 'clip'; c = last;
      ui();
      caption(clips[last].text);
      playClip(clips[last], myRun, function (wait) {
        timer = setTimeout(function () {
          if (myRun !== run) return;
          ad.finish();
          on = false; paused = false; mode = 'idle';
          if (activeVoice === ctl) activeVoice = null;
          ui();
          ad.end();
        }, Math.max(500, wait));
      });
    };
    ctl.start = start; ctl.stop = stop; ctl.pause = pause; ctl.resume = resume;
    ctl.toggle = function () { if (paused) resume(); else pause(); };
    ctl.isOn = function () { return on; };
    ctl.dispose = function () { run += 1; quiet(); if (activeVoice === ctl) activeVoice = null; };
    tog.addEventListener('click', function () { if (on) stop(); else start(); });
    if (pauseBtn) pauseBtn.addEventListener('click', ctl.toggle);
    if (stopBtn) stopBtn.addEventListener('click', stop);
    return ctl;
  }
  window.owAicVoice = { create: createVoice };

  function init(root) {
    if (root.getAttribute('data-aic-ready')) return;
    root.setAttribute('data-aic-ready', '1');
    var mode = root.getAttribute('data-aic-mode') || 'autoplay';
    var name = root.getAttribute('data-aic-name') || 'The employee';
    var short = root.getAttribute('data-aic-short') || name;
    var screen = $(root, '[data-aic-screen]');
    var wins = screen ? $$(screen, '[data-aic-app]') : [];
    var dock = screen ? $$(screen, '[data-aic-dock]') : [];
    var cursor = screen ? $(screen, '.ow-aic-cursor') : null;
    var steps = $$(root, '[data-aic-step]').map(function (li) {
      var title = $(li, '.ch-checklist__title'), time = $(li, 'time');
      return {
        li: li,
        app: li.getAttribute('data-app') || 'inbox',
        state: li.getAttribute('data-state') || 'done',
        time: time ? time.getAttribute('data-aic-time') || '' : '',
        label: title && title.lastChild ? title.lastChild.textContent : ''
      };
    });
    var tl = $$(root, '[data-aic-tl]');

    initTabs(root);
    initPanelToggle(root, name);
    initFold(root, steps.length);
    initBig(root, name);

    function showApp(app, moveCursor) {
      var shown = null;
      wins.forEach(function (w) { var on = w.getAttribute('data-aic-app') === app; w.hidden = !on; if (on) shown = w; });
      dock.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-aic-dock') === app); });
      if (shown && app === 'doc' && moveCursor) typeDoc(shown);
      if (!cursor || !shown || !moveCursor) return;
      var target = $(shown, '[data-aic-target]') || shown;
      var s = screen.getBoundingClientRect(), t = target.getBoundingClientRect();
      if (!s.width) return;
      var x = t.left - s.left + Math.min(t.width * 0.6, 120), y = t.top - s.top + t.height * 0.55;
      cursor.style.transform = 'translate(' + Math.round(x) + 'px,' + Math.round(y) + 'px)';
    }

    /* The draft types out word by word the first time the Draft window opens during playback. */
    function typeDoc(win) {
      var p = $(win, '[data-aic-type]');
      if (!p || p.getAttribute('data-typed') || reduced.matches) return;
      p.setAttribute('data-typed', '1');
      var caret = $(p, '.ow-aic-caret'), html = p.innerHTML.replace(/<span class="ow-aic-caret"><\/span>$/, '');
      var parts = html.split(/(\s+|<br\s*\/?>)/), k = 0;
      p.innerHTML = ''; if (caret) p.appendChild(caret);
      (function next() {
        if (k >= parts.length) return;
        var chunk = parts.slice(k, k + 3).join(''); k += 3;
        if (caret) caret.insertAdjacentHTML('beforebegin', chunk); else p.insertAdjacentHTML('beforeend', chunk);
        setTimeout(next, 45);
      })();
    }
    var resultEl = $(root, '[data-aic-result]');

    if (!steps.length) return;
    // A still story remains paused at its authored screen, with manual playback available.
    var stillAt = mode === 'static' ? steps.filter(function (s) { return s.state === 'current'; }).pop() ||
      steps.filter(function (s) { return s.state === 'waiting'; })[0] ||
      steps.filter(function (s) { return s.state === 'done'; }).pop() || steps[0] : null;

    /* The run. i is the step at work; phase: run | ask | mine | ended (before: the phase under "mine"). */
    var wait = -1;
    steps.forEach(function (s, k) { if (wait < 0 && s.state === 'waiting' && (mode !== 'static' || $(root, '[data-aic-ask]'))) wait = k; });
    var i = stillAt ? steps.indexOf(stillAt) : 0, phase = i === wait ? 'ask' : 'run', before = 'run', decided = null, answer = '', stopped = false;
    var playing = false, wanted = mode === 'autoplay' && !reduced.matches, inView = false, timer = null;
    var vo = null, vOn = false;   // narration (createVoice above): vOn is true while the voice drives the scene

    var state = $(root, '[data-aic-state]');
    var control = $(root, '[data-aic-control]');
    var playBtn = $(root, '[data-aic-play]');
    var progress = $(root, '[data-aic-progress]');
    var bar = $(root, '[data-aic-controls]');
    var headStatus = $(root, '[data-aic-head-status]');
    var now = $(root, '[data-aic-now]');
    var count = $(root, '[data-aic-count]');
    var stopBtn = $(root, '[data-aic-stop]');
    var takeBtn = $(root, '[data-aic-take]');
    if (bar) bar.hidden = false;

    function isEnded() { return phase === 'ended' || (phase === 'mine' && before === 'ended'); }
    function stateOf(k) {
      if (isEnded()) {
        if (k < i) return 'done';
        if (k === i && !stopped && (decided === null || decided === 'approved')) return 'done';
        return 'next';
      }
      if (k < i) return 'done';
      if (k > i) return 'next';
      return k === wait && decided === null ? 'waiting' : 'current';
    }
    function labelOf(k) {
      if (k === wait && decided === 'approved') return 'You approved it';
      if (k === wait && decided === 'declined') return 'You declined it';
      if (k === wait && decided === 'answered') return 'You answered in your own words';
      return steps[k].label;
    }

    function render() {
      var ended = isEnded();
      if (resultEl) resultEl.hidden = !(ended || decided === 'approved');
      steps.forEach(function (s, k) {
        var st = stateOf(k);
        s.li.className = 'is-' + st;
        s.li.setAttribute('data-state', st);
        var mark = s.li.firstElementChild;
        if (mark && !mark.classList.contains('ch-checklist__title')) mark.outerHTML = MARK[st];
        var title = $(s.li, '.ch-checklist__title');
        if (title) title.innerHTML = '<span class="os-sr-only">' + WORD[st] + '</span>' + escapeHtml(labelOf(k));
        setText($(s.li, 'time'), st === 'next' ? '' : s.time);
        var row = tl[k];
        if (row) {
          row.className = 'wk-step wk-step--' + TL_CLASS[st];
          var m = $(row, '.wk-step__mark');
          if (m) { m.className = TL_MARK[st][0]; m.innerHTML = TL_MARK[st][1]; }
          setText($(row, '.wk-step__title'), labelOf(k));
          setText($(row, '.wk-step__time'), st === 'next' ? '' : s.time);
        }
      });
      // The card's state, as the app words it: Live while the employee drives, Paused while a person has it, Ended.
      var pill = ended ? ['Ended', 'neutral'] : phase === 'ask' ? ['Waiting', 'warning'] : !playing ? ['Paused', 'neutral'] : ['Live', 'success'];
      if (state) { state.className = 'os-pc__live wk-pc__state wk-pc__state--' + pill[1]; setText($(state, 'span'), pill[0]); }
      var text = phase === 'mine' ? (ended ? 'You have control.' : 'You have control. ' + short + ' is paused until you give it back.')
        : ended ? 'The task has ended. You can still look around, or take over.'
        : short + ' is working. Take over to pause ' + short + ' and use the computer yourself.';
      if (control && control.textContent !== text) control.innerHTML = '<span>' + escapeHtml(text) + '</span>';
      if (takeBtn) {
        var holding = phase === 'mine';
        takeBtn.innerHTML = icon(holding ? 'bot' : 'arrow-up', 'os-icon--16') + '<span>' + (holding ? 'Give back' : 'Take over') + '</span>';
      }
      root.classList.toggle('is-mine', phase === 'mine');
      root.classList.toggle('is-playing', playing);
      if (headStatus) {
        var hs = phase === 'ask' ? ['warning', 'Waiting for you'] : phase === 'mine' && !ended ? ['warning', 'Paused'] : ended ? ['', 'Idle'] : ['info', 'Working'];
        headStatus.className = 'os-status' + (hs[0] ? ' os-status--' + hs[0] : '') + ' ch-head__status';
        setText(headStatus, hs[1]);
      }
      if (now) {
        var unfinished = stopped || decided === 'declined' || decided === 'answered';
        now.innerHTML = phase === 'ask' ? '<span class="os-dot os-dot--warning" aria-hidden="true"></span><span>' + escapeHtml(name) + ' is waiting for you</span>'
          : phase === 'mine' && !ended ? icon('pause', 'os-icon--16') + '<span>' + escapeHtml(name) + ' is paused</span>'
          : ended ? '<span class="os-status' + (unfinished ? '' : ' os-status--success') + '">' + (stopped ? 'Stopped' : unfinished ? 'Not finished' : 'Done') + '</span>'
          : '<span class="os-orb" aria-hidden="true"></span><span class="os-thinking">' + escapeHtml(labelOf(i)) + '</span>';
      }
      setText(count, 'Step ' + Math.min(i + 1, steps.length) + ' of ' + steps.length);
      if (stopBtn) stopBtn.disabled = ended;
      var ask = $(root, '[data-aic-ask]');
      if (ask) {
        ask.classList.toggle('is-asking', phase === 'ask');
        showOutcome(ask, decided, answer);
        if (stopped && decided === null) ask.classList.add('is-decided');
      }
      if (playBtn) {
        var playLabel = ended ? 'Replay' : playing ? 'Pause' : 'Play';
        playBtn.setAttribute('aria-label', playLabel);
        playBtn.title = playLabel;
        playBtn.innerHTML = icon(playing ? 'pause' : 'play');
        playBtn.disabled = phase === 'ask';
      }
      if (progress) {
        var done = ended ? i + (stopped || decided === 'declined' || decided === 'answered' ? 0 : 1) : i;
        progress.setAttribute('aria-valuenow', done);
        progress.setAttribute('aria-valuetext', phase === 'ask' ? 'Waiting for your approval' : ended ? 'Ended' : 'Step ' + (i + 1) + ' of ' + steps.length);
        var fill = $(progress, '[data-aic-fill]');
        if (fill) fill.style.width = (done / steps.length * 100) + '%';
      }
      showApp(steps[Math.min(i, steps.length - 1)].app, playing && !reduced.matches);
    }

    function schedule() {
      clearTimeout(timer);
      timer = null;
      if (vOn) return;   // narrated: the clips drive the steps, not the timer
      if (playing && inView && !document.hidden) timer = setTimeout(function () { advance(); schedule(); }, TICK_MS);
    }
    function setPlaying(on) {
      playing = !!on && inView && phase === 'run';
      if (playing) {
        if (activeRoot && activeRoot !== root) activeRoot.dispatchEvent(new Event('aic:pause'));
        activeRoot = root;
      } else if (activeRoot === root) activeRoot = null;
      render();
      schedule();
    }
    /* Sets the phase under the person's control, or the phase itself. */
    function become(p) {
      if (phase === 'mine') before = p; else phase = p;
      if (p !== 'run') playing = false;
    }
    /* One step on. At the approval step the employee stops and asks. */
    function advance() {
      if (isEnded() || phase === 'ask' || (phase === 'mine' && before === 'ask')) return;
      if (i === wait && decided === null) { become('ask'); render(); return; }
      if (i >= steps.length - 1) { become('ended'); render(); return; }
      i += 1;
      if (i === wait && decided === null) become('ask');
      render();
    }
    function rewind() {
      if (vOn && vo) vo.stop();
      i = 0; decided = null; answer = ''; stopped = false; phase = 'run'; before = 'run';
      var own = $(root, '[data-aic-own] textarea');
      if (own) own.value = '';
      wanted = mode === 'autoplay' && !reduced.matches;
      setPlaying(wanted);
      root.dispatchEvent(new CustomEvent('aic:rewind', { bubbles: true }));
    }
    function decide(kind, text) {
      if (isEnded() || decided !== null) return;
      if (wait >= 0) i = wait;
      decided = kind;
      answer = text || '';
      root.dispatchEvent(new CustomEvent('aic:decision', { bubbles: true, detail: { kind: kind, draft: ($(root, '.ow-aic__draft') || {}).textContent || '' } }));
      if (kind !== 'approved' || i >= steps.length - 1) { become('ended'); render(); schedule(); if (vOn && vo) vo.decision(kind); return; }
      become('run');
      i += 1;
      if (vOn && vo) { playing = true; render(); vo.decision(kind); return; }
      setPlaying(wanted);
    }
    /* The narration adapter: the voice moves the scene from beat to beat. task: the scene as it opens (step one at
       work); s1 to s3: steps one to three; ask: the approval card is raised and the scene waits. */
    var vAd = {
      begin: function () {
        vOn = true; wanted = false; clearTimeout(timer);
        i = 0; decided = null; answer = ''; stopped = false; phase = 'run'; before = 'run';
        var own = $(root, '[data-aic-own] textarea');
        if (own) own.value = '';
        if (activeRoot && activeRoot !== root) activeRoot.dispatchEvent(new Event('aic:pause'));
        activeRoot = root;
        playing = true;
        render();
      },
      cue: function (beat) {
        var m = /^s(\d)$/.exec(beat), last = wait >= 0 ? wait - 1 : steps.length - 1;
        if (phase === 'mine') phase = before;
        if (beat === 'ask') {
          i = wait >= 0 ? wait : steps.length - 1;
          if (decided === null) become('ask');
          render();
          return 0;
        }
        i = beat === 'task' || !m ? 0 : Math.max(0, Math.min(parseInt(m[1], 10) - 1, last));
        phase = 'run'; playing = true;
        render();
        return beat === 'task' ? 0 : TICK_MS;
      },
      hold: function (on) { if (phase === 'run') playing = !on; render(); },
      finish: function () { i = steps.length - 1; become('ended'); render(); },
      end: function () { vOn = false; playing = false; if (activeRoot === root) activeRoot = null; render(); }
    };
    vo = window.owAicVoice ? window.owAicVoice.create(root, vAd) : null;
    wireAsk(root, decide);
    /* Employee job dialogs change workspace/skill without replacing the app computer. */
    function setScenario(data) {
      if (!data || !Array.isArray(data.steps) || data.steps.length !== steps.length) return;
      steps.forEach(function (step, k) { step.label = data.steps[k]; });
      setText($(root, '.ow-aic__task'), data.task);
      setText($(root, '.ch-ask__title'), data.approval);
      setText($(root, '.app-facts dd'), data.task);
      setText($(root, '[data-aic-app="inbox"] li.is-open > span'), data.task);
      setText($(root, '.ow-aic-sheet tr:nth-child(2) td:first-child'), data.task);
      $$(root, '.ow-aic-page li:first-child').forEach(function (el) { setText(el, data.task); });
      $$(root, '[data-aic-app="browser"] .ow-aic-win__title, .ow-aic-shot .ow-aic-win__title').forEach(function (el) { setText(el, data.task); });
      setText($(root, '[data-aic-app="doc"] .ow-aic-win__title'), data.approval);
      setText($(root, '.ow-aic-doc'), data.steps[1] || data.task);
      rewind();
    }
    root.aicSetScenario = setScenario;
    if (root.aicScenario) setScenario(root.aicScenario);


    root.aicReplay = rewind;
    if (playBtn) playBtn.addEventListener('click', function () {
      if (vOn && vo) { vo.toggle(); return; }
      if (isEnded()) { rewind(); wanted = true; setPlaying(true); }
      else { wanted = !playing; setPlaying(wanted); }
    });
    /* Take over pauses the employee and hands the person the screen; Give back carries on where it was
       (DesktopPanel.tsx: take and giveBack). */
    if (takeBtn) takeBtn.addEventListener('click', function () {
      if (phase === 'mine') {
        phase = before;
        if (phase === 'run') { setPlaying(wanted); return; }
      } else {
        before = phase; phase = 'mine'; playing = false;
        if (vOn && vo) vo.pause();
        setPlaying(false); return;
      }
      render(); schedule();
    });
    if (stopBtn) stopBtn.addEventListener('click', function () { if (vOn && vo) vo.stop(); stopped = true; become('ended'); render(); schedule(); });

    // It plays only while it is on screen; a hidden browser tab holds it.
    var viewObserver;
    if ('IntersectionObserver' in window) {
      // "On screen": a third of it shows, or, when it is taller than the window, it fills half the window.
      viewObserver = new IntersectionObserver(function (entries) {
        var e = entries[0], seen = e.intersectionRect.height;
        inView = e.isIntersecting && (e.intersectionRatio >= 0.35 || seen >= window.innerHeight * 0.5);
        if (!inView && vOn && vo) vo.pause();
        if (!inView && playing) setPlaying(false);
        else if (inView && wanted && !playing && phase === 'run') setPlaying(true);
        else schedule();
      }, { threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.75, 1] });
      viewObserver.observe(root);
    } else {
      inView = true;
    }
    function visibility() { if (document.hidden) { if (vOn && vo) vo.pause(); setPlaying(false); } else if (wanted) setPlaying(true); }
    function motion() { if (reduced.matches) { wanted = false; setPlaying(false); } }
    function pauseOther() { wanted = false; if (vOn && vo) vo.pause(); setPlaying(false); }
    root.addEventListener('aic:pause', pauseOther);
    document.addEventListener('visibilitychange', visibility);
    if (reduced.addEventListener) reduced.addEventListener('change', motion);
    root.aicDispose = function () { clearTimeout(timer); if (vo) vo.dispose(); if (viewObserver) viewObserver.disconnect(); document.removeEventListener('visibilitychange', visibility); if (reduced.removeEventListener) reduced.removeEventListener('change', motion); if (activeRoot === root) activeRoot = null; };
    render();
    if (inView && wanted) setPlaying(true);
  }

  var pending = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) { if (entry.isIntersecting) { pending.unobserve(entry.target); init(entry.target); } });
  }, { threshold: 0.01 }) : null;
  function boot() { $$(document, '[data-aic]').forEach(function (root) { if (root.hasAttribute('data-aic-observed')) return; root.setAttribute('data-aic-observed', '1'); if (pending) pending.observe(root); else init(root); }); }
  document.addEventListener('shopify:section:unload', function (e) { $$(e.target, '[data-aic]').forEach(function (root) { if (pending) pending.unobserve(root); if (root.aicDispose) root.aicDispose(); }); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
